import React, { useMemo, useState } from 'react';
import { partnerApiService } from '../services/api';
import { apiFieldErrors } from '../services/apiErrors';
import { Discount, DiscountKind, DiscountValueType } from '../types/api';
import type { Locale } from '../locales';
import { useLanguage } from '../contexts/LanguageContext';
import { FormShell, FormFoot } from './form/FormShell';
import { Toast, useToast } from './form/Toast';
import { formatDate, today } from '../utils/period';
import { PricedItem, comboPieces, comboRegularPrice } from '../utils/discountPricing';
import { fill } from '../utils/template';
import {
  ComboLine,
  DiscountDraft,
  DiscountErrors,
  DiscountField,
  DiscountTarget,
  clampQuantity,
  discountToDraft,
  draftToRequest,
  emptyDraft,
  itemsInCategory,
  mapServerErrors,
  placeErrors,
  priceErrors,
  syncComboLines,
  validateDraft,
} from './discount/discountDraft';
import { DiscountMainCard } from './discount/DiscountMainCard';
import { DiscountTargetCard } from './discount/DiscountTargetCard';
import { DiscountValueCard } from './discount/DiscountValueCard';
import { ComboContentsCard, ComboPriceCard, ComboRow, KnownItem } from './discount/ComboCards';
import { ScheduleCards } from './discount/ScheduleCards';
import { useDiscountCatalog } from './discount/useDiscountCatalog';

interface DiscountFormProps {
  discount?: Discount;
  onSave: () => void;
  onCancel: () => void;
}

/** Пауза перед закрытием формы: партнёр успевает увидеть «Скидка создана». */
const CLOSE_AFTER_SAVE_MS = 800;

/** Порядок полей на экране: тост называет первую ошибку сверху. */
const FIELD_ORDER: DiscountField[] = [
  'general',
  'name',
  'target',
  'combo',
  'value',
  'bundle',
  'time',
  'weekdays',
  'starts_at',
  'ends_at',
];

function firstError(errors: DiscountErrors): string | undefined {
  return FIELD_ORDER.map((field) => errors[field]).find(Boolean);
}

function withoutFields(errors: DiscountErrors, fields: DiscountField[]): DiscountErrors {
  return Object.fromEntries(
    Object.entries(errors).filter(([key]) => !fields.includes(key as DiscountField)),
  ) as DiscountErrors;
}

function isDefined<T>(value: T | undefined): value is T {
  return value !== undefined;
}

/** Текст сервера, если он есть; «Network Error» на английском партнёру не показываем. */
function serverMessage(error: unknown): string | null {
  const message = (error as { response?: { data?: { message?: unknown } } })?.response?.data?.message;

  return typeof message === 'string' && message ? message : null;
}

/** Дата в подвале: по-русски «30 июня», на других языках — числами. */
function footDate(locale: Locale, ymd: string): string {
  if (locale === 'ru') return formatDate(ymd);
  const [year, month, day] = ymd.split('-');

  return `${day}.${month}.${year}`;
}

/**
 * Форма скидки (ADR-026): имя → вид «На позиции» / «Комбо» → цель и размер
 * либо состав и цена набора → расписание и срок.
 *
 * Состояние — один черновик (`discountDraft.ts`): там же разбор ответа,
 * тело запроса и проверки, повторяющие серверные. Ошибки 422 раскладываются
 * под те же поля.
 */
export function DiscountForm({ discount, onSave, onCancel }: DiscountFormProps) {
  const { t, locale } = useLanguage();
  const strings = t.discounts;
  const catalog = useDiscountCatalog();

  const [draft, setDraft] = useState<DiscountDraft>(() =>
    discount ? discountToDraft(discount) : emptyDraft(),
  );
  const [errors, setErrors] = useState<DiscountErrors>({});
  const [loading, setLoading] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [toast, setToast] = useToast();

  const isEdit = !!discount;

  /** Правка черновика снимает ошибки затронутых полей и общую строку. */
  const change = (recipe: (prev: DiscountDraft) => Partial<DiscountDraft>, fields: DiscountField[]) => {
    setDraft((prev) => ({ ...prev, ...recipe(prev) }));
    setIsDirty(true);
    setErrors((prev) => withoutFields(prev, [...fields, 'general']));
  };

  // Цены, которые знает форма. Запасной источник — сама скидка: меню могло
  // не загрузиться, а превью и сумме комбо цены нужны и тогда.
  const knownItems = useMemo(() => {
    const map = new Map<number, KnownItem>();
    (discount?.menu_items ?? []).forEach((item) =>
      map.set(item.id, { id: item.id, name: item.name, price: item.price, status: item.status }),
    );
    (discount?.combo_items ?? []).forEach((line) =>
      map.set(line.menu_item_id, { id: line.menu_item_id, name: line.name, price: line.price }),
    );
    catalog.items.forEach((item) =>
      map.set(item.id, { id: item.id, name: item.name, price: item.price, status: item.status }),
    );
    return map;
  }, [discount, catalog.items]);

  /** Позиции цели групповой скидки; null — цены неизвестны. */
  const targetItems = useMemo<PricedItem[] | null>(() => {
    if (draft.target === 'category') {
      if (draft.categoryId == null) return [];
      if (catalog.status !== 'ready') return null;
      return itemsInCategory(catalog.categories, catalog.items, draft.categoryId);
    }

    const found = draft.itemIds.map((id) => knownItems.get(id));
    if (found.every(isDefined)) return found;

    // Позиции нет ни в меню, ни в скидке: удалена. Считаем по оставшимся,
    // если меню загружено, иначе цены неизвестны.
    return catalog.status === 'ready' ? found.filter(isDefined) : null;
  }, [draft.target, draft.categoryId, draft.itemIds, catalog.status, catalog.categories, catalog.items, knownItems]);

  const comboRows = useMemo<ComboRow[]>(
    () => draft.comboLines.map((line) => ({ line, item: knownItems.get(line.menuItemId) })),
    [draft.comboLines, knownItems],
  );

  const pricedComboLines = useMemo(() => {
    const priced = comboRows.map(({ line, item }) =>
      item ? { ...item, quantity: line.quantity } : undefined,
    );
    return priced.every(isDefined) ? priced : null;
  }, [comboRows]);

  // Пустой состав — обычной цены ещё нет: «0 ₸» читалось бы как цена.
  const regularPrice =
    pricedComboLines && pricedComboLines.length > 0 ? comboRegularPrice(pricedComboLines) : null;
  const pricing = { targetItems, comboLines: pricedComboLines };

  // Ошибки на экране: отправки и сервера — поверх тех, что видны по ходу
  // ввода (тенге больше цены, комбо не дешевле набора).
  const shown = placeErrors({ ...priceErrors(draft, pricing, strings), ...errors }, draft.kind);

  const setKind = (kind: DiscountKind) =>
    change(() => ({ kind }), ['target', 'value', 'combo', 'bundle']);

  const setTarget = (target: DiscountTarget) =>
    change((prev) => {
      // «Раздел → позиции» при выбранном разделе: галочки ставятся всем его
      // позициям, и партнёр снимает лишние — ровно то, чего раньше ждали от
      // снятых галочек у выбранного раздела.
      const seed =
        target === 'items' && prev.itemIds.length === 0 && prev.categoryId != null && catalog.status === 'ready'
          ? itemsInCategory(catalog.categories, catalog.items, prev.categoryId).map((item) => item.id)
          : prev.itemIds;
      return { target, itemIds: seed };
    }, ['target', 'value']);

  const setComboLines = (recipe: (lines: ComboLine[]) => ComboLine[]) =>
    change((prev) => ({ comboLines: recipe(prev.comboLines) }), ['combo', 'bundle']);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    const found = placeErrors(validateDraft(draft, pricing, strings), draft.kind);
    if (Object.keys(found).length > 0) {
      setErrors(found);
      setToast({ message: firstError(found) ?? strings.saveFailed, tone: 'error' });
      return;
    }

    setErrors({});
    setLoading(true);
    let saved = false;

    try {
      const body = draftToRequest(draft);
      if (discount) await partnerApiService.updateDiscount(discount.id, body);
      else await partnerApiService.createDiscount(body);

      saved = true;
      setIsDirty(false);
      setToast({ message: isEdit ? strings.updated : strings.created, tone: 'success' });
      setTimeout(() => onSave(), CLOSE_AFTER_SAVE_MS);
    } catch (error: unknown) {
      console.error('Ошибка сохранения скидки:', error);

      // 422 Laravel — объект `{поле: [тексты]}`: показываем под полями.
      const fromServer = placeErrors(mapServerErrors(apiFieldErrors(error)), draft.kind);
      if (Object.keys(fromServer).length > 0) {
        setErrors(fromServer);
        setToast({ message: firstError(fromServer) ?? strings.saveFailed, tone: 'error' });
      } else {
        setToast({ message: serverMessage(error) ?? strings.saveFailed, tone: 'error' });
      }
    } finally {
      // После успеха кнопки остаются запертыми до закрытия формы: второй клик
      // в эти 800 мс создал бы дубликат.
      if (!saved) setLoading(false);
    }
  };

  const saveLabel = loading ? strings.saving : isEdit ? strings.save : strings.create;

  const scopeShort =
    draft.kind === 'combo'
      ? draft.comboLines.length > 0
        ? fill(strings.eyebrowPieces, { count: comboPieces(draft.comboLines) })
        : strings.eyebrowNothing
      : draft.target === 'category'
        ? draft.categoryId != null
          ? strings.eyebrowCategory
          : strings.eyebrowNothing
        : draft.itemIds.length > 0
          ? fill(strings.eyebrowItems, { count: draft.itemIds.length })
          : strings.eyebrowNothing;
  const kindLabel = draft.kind === 'combo' ? strings.kindCombo : strings.kindGroup;

  // Отложенный старт меняет обещание в подвале: «включается сразу» — неправда,
  // если партнёр поставил дату начала будущим числом.
  const startsLater = !!draft.startsAt && draft.startsAt > today();
  const footNote = startsLater
    ? fill(strings.footLater, { date: footDate(locale, draft.startsAt) })
    : strings.footNow;

  return (
    <>
      <FormShell
        eyebrow={`${strings.eyebrow} · ${kindLabel} · ${scopeShort}`}
        title={isEdit ? strings.titleEdit : strings.titleNew}
        isDirty={isDirty}
        dirtyLabel={strings.dirty}
        cleanLabel={strings.clean}
        actions={
          <>
            <button type="button" className="qp-btn qp-btn--outline" onClick={onCancel} disabled={loading}>
              {strings.cancel}
            </button>
            <button type="submit" form="discount-form" className="qp-btn qp-btn--primary" disabled={loading}>
              {saveLabel}
            </button>
          </>
        }
        footer={
          <FormFoot note={footNote}>
            <button type="submit" form="discount-form" className="qp-btn qp-btn--primary" disabled={loading}>
              {saveLabel}
            </button>
          </FormFoot>
        }
      >
        <form id="discount-form" onSubmit={handleSubmit} noValidate className="qp-stack">
          {shown.general && (
            <div className="qp-notice qp-notice--error" role="alert">
              {shown.general}
            </div>
          )}

          <DiscountMainCard
            name={draft.name}
            kind={draft.kind}
            nameError={shown.name}
            disabled={loading}
            onNameChange={(name) => change(() => ({ name }), ['name'])}
            onKindChange={setKind}
          />

          {draft.kind === 'group' ? (
            <>
              <DiscountTargetCard
                catalog={catalog}
                target={draft.target}
                categoryId={draft.categoryId}
                itemIds={draft.itemIds}
                targetCount={targetItems ? targetItems.length : null}
                error={shown.target}
                disabled={loading}
                onTargetChange={setTarget}
                onCategoryChange={(categoryId) => change(() => ({ categoryId }), ['target', 'value'])}
                onItemsChange={(itemIds) => change(() => ({ itemIds }), ['target', 'value'])}
              />
              <DiscountValueCard
                valueType={draft.valueType}
                percent={draft.percent}
                amount={draft.amount}
                targetItems={targetItems}
                targetChosen={draft.target === 'category' ? draft.categoryId != null : draft.itemIds.length > 0}
                error={shown.value}
                disabled={loading}
                onValueTypeChange={(valueType: DiscountValueType) => change(() => ({ valueType }), ['value'])}
                onPercentChange={(percent) => change(() => ({ percent }), ['value'])}
                onAmountChange={(amount) => change(() => ({ amount }), ['value'])}
              />
            </>
          ) : (
            <>
              <ComboContentsCard
                catalog={catalog}
                rows={comboRows}
                regularPrice={regularPrice}
                error={shown.combo}
                disabled={loading}
                onItemsChange={(itemIds) => setComboLines((lines) => syncComboLines(lines, itemIds))}
                onQuantityChange={(menuItemId, quantity) =>
                  setComboLines((lines) =>
                    lines.map((line) =>
                      line.menuItemId === menuItemId ? { ...line, quantity: clampQuantity(quantity) } : line,
                    ),
                  )
                }
                onRemove={(menuItemId) =>
                  setComboLines((lines) => lines.filter((line) => line.menuItemId !== menuItemId))
                }
              />
              <ComboPriceCard
                bundlePrice={draft.bundlePrice}
                regularPrice={regularPrice}
                error={shown.bundle}
                disabled={loading}
                onChange={(bundlePrice) => change(() => ({ bundlePrice }), ['bundle'])}
              />
            </>
          )}

          <ScheduleCards
            timeStart={draft.timeStart}
            timeEnd={draft.timeEnd}
            weekdays={draft.weekdays}
            startsAt={draft.startsAt}
            endsAt={draft.endsAt}
            errors={shown}
            disabled={loading}
            onTimeStartChange={(timeStart) => change(() => ({ timeStart }), ['time'])}
            onTimeEndChange={(timeEnd) => change(() => ({ timeEnd }), ['time'])}
            onWeekdayToggle={(weekday) =>
              change(
                (prev) => ({
                  weekdays: prev.weekdays.includes(weekday)
                    ? prev.weekdays.filter((day) => day !== weekday)
                    : [...prev.weekdays, weekday],
                }),
                ['weekdays'],
              )
            }
            onWeekdaysSet={(weekdays) => change(() => ({ weekdays }), ['weekdays'])}
            onStartsAtChange={(startsAt) => change(() => ({ startsAt }), ['starts_at', 'ends_at'])}
            onEndsAtChange={(endsAt) => change(() => ({ endsAt }), ['ends_at'])}
          />
        </form>
      </FormShell>

      <Toast toast={toast} />
    </>
  );
}
